import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Home, Youtube, Music2, Sparkles, Building2, PartyPopper, Wand2,
  Film, Share2, PenLine, Megaphone, Layers,
  Calendar, CalendarDays, CalendarClock, Check, ChevronRight, ChevronLeft, Loader2,
} from "lucide-react";

const PERSONAS = [
  { id: "realtor",    label: "Makelaar",              desc: "Vastgoedvideo's & listings",  icon: Home },
  { id: "youtuber",   label: "YouTuber",              desc: "Long-form video content",     icon: Youtube },
  { id: "tiktoker",   label: "TikToker",              desc: "Vertical short-form clips",   icon: Music2 },
  { id: "influencer", label: "Influencer",            desc: "Instagram, Reels, stories",   icon: Sparkles },
  { id: "business",   label: "Bedrijf / Marketing",   desc: "Ads, campagnes, branding",    icon: Building2 },
  { id: "fun",        label: "Voor de lol",           desc: "Creatieve projecten",         icon: PartyPopper },
  { id: "other",      label: "Anders",                desc: "Ik ontdek het onderweg",      icon: Wand2 },
];

const GOALS = [
  { id: "property_video", label: "Vastgoedvideo's",        desc: "Foto's naar cinematische video's", icon: Film },
  { id: "social_clips",   label: "Social clips",           desc: "Instagram / TikTok / YouTube",     icon: Share2 },
  { id: "blog_articles",  label: "Blog & artikelen",       desc: "SEO-teksten, blogs, captions",     icon: PenLine },
  { id: "ad_campaigns",   label: "Reclamecampagnes",       desc: "Ads voor Meta / Google / TikTok",  icon: Megaphone },
  { id: "everything",     label: "Alles",                  desc: "Ik wil de volledige suite",        icon: Layers },
];

const FREQ = [
  { id: "daily",        label: "Dagelijks",   desc: "Meerdere posts per dag",          icon: Calendar },
  { id: "weekly",       label: "Wekelijks",   desc: "Een paar keer per week",          icon: CalendarDays },
  { id: "occasionally", label: "Af en toe",   desc: "Wanneer ik iets nieuws heb",      icon: CalendarClock },
];

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    persona: "",
    content_goal: "",
    frequency: "",
  });
  const [busy, setBusy] = useState(false);

  const totalSteps = 3;
  const stepConfig = [
    {
      key: "persona",
      title: "Waarvoor gebruik je MediaOthers?",
      subtitle: "Kies wat het beste bij je past — we passen je dashboard hierop aan.",
      options: PERSONAS,
    },
    {
      key: "content_goal",
      title: "Wat wil je vooral maken?",
      subtitle: "We tonen de juiste tools bovenaan je dashboard.",
      options: GOALS,
    },
    {
      key: "frequency",
      title: "Hoe vaak wil je content maken?",
      subtitle: "Zo weten we welk plan het beste bij je past.",
      options: FREQ,
    },
  ];

  const current = stepConfig[step];
  const value = answers[current.key];

  const pick = (id) => setAnswers((a) => ({ ...a, [current.key]: id }));

  const next = () => {
    if (!value) {
      toast.error("Kies een optie om verder te gaan");
      return;
    }
    if (step < totalSteps - 1) setStep(step + 1);
    else submit();
  };

  const back = () => step > 0 && setStep(step - 1);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/users/onboarding", answers);
      await refreshUser();
      toast.success("Welkom bij MediaOthers!");
      nav("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Er ging iets mis");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0b0b0d]">
      <div className="w-full max-w-3xl">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="onboarding-progress">
          {stepConfig.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === step ? "w-12 bg-amber-500" : i < step ? "w-6 bg-amber-500/60" : "w-6 bg-white/10"
              }`}
            />
          ))}
        </div>

        <div className="text-center mb-10">
          <div className="font-mono-tech text-[10px] text-amber-400 mb-3">
            STAP {step + 1} · VAN {totalSteps} · WELKOM {user?.name?.split(" ")[0]?.toUpperCase() || ""}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
            {current.title}
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">{current.subtitle}</p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10"
            data-testid={`onboarding-options-step-${step}`}
          >
            {current.options.map((opt) => {
              const Icon = opt.icon;
              const active = value === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => pick(opt.id)}
                  data-testid={`onboarding-option-${opt.id}`}
                  className={`text-left rounded-lg border p-5 transition-all duration-200 group ${
                    active
                      ? "border-amber-500 bg-amber-500/10 ring-1 ring-amber-500/40"
                      : "border-white/5 bg-[#121214] hover:border-white/20 hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className={`w-10 h-10 rounded-md flex items-center justify-center transition-colors ${
                        active ? "bg-amber-500/20" : "bg-white/5 group-hover:bg-white/10"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${active ? "text-amber-400" : "text-zinc-400"}`}
                        strokeWidth={1.5}
                      />
                    </div>
                    {active && (
                      <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                  <div className="font-heading font-bold text-base tracking-tight">{opt.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">{opt.desc}</div>
                </button>
              );
            })}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between">
          <Button
            onClick={back}
            disabled={step === 0 || busy}
            variant="outline"
            data-testid="onboarding-back"
            className="bg-transparent border-white/15 hover:bg-white/5 disabled:opacity-30"
          >
            <ChevronLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
            Terug
          </Button>
          <Button
            onClick={next}
            disabled={busy}
            data-testid="onboarding-next"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-11 px-6"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…
              </>
            ) : step === totalSteps - 1 ? (
              <>
                Klaar <Check className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </>
            ) : (
              <>
                Volgende <ChevronRight className="w-4 h-4 ml-1.5" strokeWidth={1.5} />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
